/**
 * EmbedAuthStates - Shared UI components for embed auth states
 *
 * Provides consistent loading, error, and unauthorized states across embed pages.
 * Following existing styling patterns from QuoteResponseEmbedPage and ChatEmbedPage.
 */

import React from "react";
import { LoadingScreen } from "@semo/shared-ui";

interface EmbedLoadingStateProps {
  /** Loading message to display */
  message?: string;
}

/**
 * Loading state component for embed pages
 */
export function EmbedLoadingState({
  message = "잠시만 기다려주세요...",
}: EmbedLoadingStateProps) {
  return <LoadingScreen className="bg-gray-50" message={message} />;
}

interface EmbedErrorStateProps {
  /** Error title */
  title?: string;
  /** Error message to display */
  message: string;
  /** Optional retry callback */
  onRetry?: () => void;
}

/**
 * Error state component for embed pages
 */
export function EmbedErrorState({
  title = "접근 오류",
  message,
  onRetry,
}: EmbedErrorStateProps) {
  return (
    <div className="min-h-screen bg-canvas  flex items-center justify-center">
      <div className="text-center p-8 bg-white  rounded-xl max-w-md">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-100  rounded-full flex items-center justify-center">
          <svg
            className="w-8 h-8 text-red-600 "
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-slate-900  mb-2">{title}</h2>
        <p className="text-slate-600  mb-4">{message}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors"
          >
            다시 시도
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Unauthorized state component for embed pages
 */
export function EmbedUnauthorizedState() {
  return (
    <div className="min-h-screen bg-canvas  flex items-center justify-center">
      <div className="text-center p-8 bg-white  rounded-xl max-w-md">
        <h2 className="text-xl font-semibold text-slate-900  mb-2">
          접근 권한 없음
        </h2>
        <p className="text-slate-600 ">
          이 페이지에 접근할 수 있는 권한이 없습니다.
        </p>
      </div>
    </div>
  );
}
